import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Box, Drawer, Typography, Button, IconButton, Tooltip, Collapse,
  LinearProgress, Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyCircleIcon,
  FiberManualRecord as ActiveDotIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  Assignment as SummaryIcon,
  ExpandLess,
  ExpandMore,
  Foundation as FoundationIcon,
  Curtains as WindowsIcon,
  GridView as FlooringIcon,
  Kitchen as CabinetsIcon,
  CountertopsOutlined as CountertopsIcon,
  Plumbing as PlumbingIcon,
  EmojiObjects as LightingIcon,
  Microwave as AppliancesIcon,
  Air as HvacIcon,
  HomeWork as InteriorIcon,
  LocalFireDepartment as FireplaceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomer } from '../pages/CustomerContext';
import { PriceContext } from '../pages/PriceContextOptimized';
import { luxuryTextures, withLuxuryTexture } from '../styles/luxuryTextures';
import { useTheme } from '../contexts/ThemeContext';
import { PageTransition } from '../components/PageTransitionsLight';

const GOLD = '#B8860B';
const GOLD_ALPHA = 'rgba(184,134,11,0.08)';
const DRAWER_OPEN_W = 260;
const DRAWER_CLOSED_W = 60;
const HEADER_H = 64;

// Tab definitions for "Build Your Home" sub-nav — mirrors SelectionHub TABS
// sectionKeys: list of sectionKey values whose selections count toward completion.
// roomBased: if true, completion uses ":" separator instead of "-".
const BUILD_TABS = [
  { id: 'pivotals',    label: 'Pivotals',    icon: FoundationIcon,  sectionKeys: ['elevationSelections','lotTypeSelections','patioDeckSelections','basementSelections','garageSelections','ceilingsSelections'] },
  { id: 'windows',     label: 'Windows',     icon: WindowsIcon,     sectionKeys: ['windowsSelections'] },
  { id: 'flooring',    label: 'Flooring',    icon: FlooringIcon,    sectionKeys: ['flooringSelections'],  roomBased: true },
  { id: 'cabinets',    label: 'Cabinets',    icon: CabinetsIcon,    sectionKeys: ['cabinetsSelections'],  roomBased: true },
  { id: 'countertops', label: 'Countertops', icon: CountertopsIcon, sectionKeys: ['countertopsSelections','kitchenBacksplashSelections'], roomBased: true },
  { id: 'plumbing',    label: 'Plumbing',    icon: PlumbingIcon,    sectionKeys: ['kitchenFaucetSelections','sinksSelections','bathroomFixturesSelections','showerSelections','toiletsSelections'] },
  { id: 'lighting',    label: 'Lighting',    icon: LightingIcon,    sectionKeys: ['lightingSelections'],  roomBased: true },
  { id: 'appliances',  label: 'Appliances',  icon: AppliancesIcon,  sectionKeys: ['appliancesSelections'] },
  { id: 'hvac',        label: 'HVAC',        icon: HvacIcon,        sectionKeys: ['mechanicalSelections'] },
  { id: 'interior',    label: 'Interior',    icon: InteriorIcon,    sectionKeys: ['interiorSelections','doorHandlesSelections'] },
  { id: 'fireplace',   label: 'Fireplace',   icon: FireplaceIcon,   sectionKeys: ['fireplaceSelections'] },
  { id: 'exterior',    label: 'Exterior',    icon: HomeIcon,        sectionKeys: ['sidingSelections','roofSoffitSelections','porchSelections','stoneSelections','frontDoorSelections','garageDoorSelections'] },
];

function getTabCompletion(priceData, tab) {
  const bySection = priceData?.selectionsBySection || {};
  let completed = 0;
  tab.sectionKeys.forEach((prefix) => {
    const sep = tab.roomBased ? ':' : '-';
    const hasSel = Object.entries(bySection).some(
      ([k, v]) => k.startsWith(`${prefix}${sep}`) && Array.isArray(v) && v.length > 0
    );
    if (hasSel) completed++;
  });
  return { completed, total: tab.sectionKeys.length };
}

// eslint-disable-next-line no-unused-vars -- preserved for future use
function StepIcon({ status }) {
  if (status === 'completed') return <CheckIcon sx={{ fontSize: 20, color: GOLD }} />;
  if (status === 'current') return (
    <ActiveDotIcon sx={{
      fontSize: 20, color: GOLD,
      animation: 'hubPulse 2s ease-in-out infinite',
      '@keyframes hubPulse': {
        '0%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.7, transform: 'scale(1.15)' },
        '100%': { opacity: 1, transform: 'scale(1)' },
      },
    }} />
  );
  return <EmptyCircleIcon sx={{ fontSize: 20, color: '#bbb' }} />;
}

function CustomerLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, clearCustomer } = useCustomer();
  const { priceData } = useContext(PriceContext);
  const { isDarkMode } = useTheme();

  const [role, setRole] = useState(localStorage.getItem('role'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [buildExpanded, setBuildExpanded] = useState(true);

  useEffect(() => {
    setRole(localStorage.getItem('role'));
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && drawerOpen) setDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [drawerOpen]);

  const handleClearCustomer = useCallback(() => {
    clearCustomer();
    localStorage.removeItem('actingAsCustomer');
    localStorage.removeItem('customerId');
    localStorage.removeItem('priceData');
    navigate('/staff/sales-dashboard');
  }, [clearCustomer, navigate]);

  // ── Completion logic ──────────────────────────────────────────────────────

  const homeComplete = !!(priceData?.housePlan && (priceData?.lot || priceData?.lotnumber));

  const buildTabProgress = BUILD_TABS.map((tab) => ({
    ...tab,
    ...getTabCompletion(priceData, tab),
  }));
  const totalBuildSections = buildTabProgress.reduce((t, tab) => t + tab.total, 0);
  const completedBuildSections = buildTabProgress.reduce((t, tab) => t + tab.completed, 0);
  const buildPct = totalBuildSections > 0 ? Math.round((completedBuildSections / totalBuildSections) * 100) : 0;
  const buildHasStarted = completedBuildSections > 0;

  const isFinalized = priceData?.order_status === 'finalized';

  // ── Current page detection ────────────────────────────────────────────────

  const isHome = location.pathname.includes('/customer/selection') && !location.pathname.includes('/selections');
  const isBuild = location.pathname.includes('/customer/selections') || location.pathname.includes('/customer/location/');
  const isSummary = location.pathname.includes('/selection-summary');

  const step1Status = isHome ? 'current' : homeComplete ? 'completed' : 'upcoming';
  const step2Status = isBuild ? 'current' : buildHasStarted || homeComplete ? (homeComplete ? 'completed' : 'upcoming') : 'upcoming';
  const step3Status = isSummary ? 'current' : isFinalized ? 'completed' : 'upcoming';

  // ── Sidebar rendering ─────────────────────────────────────────────────────

  const drawerW = drawerOpen ? DRAWER_OPEN_W : DRAWER_CLOSED_W;

  const sidebarBg = isDarkMode
    ? 'linear-gradient(180deg, rgba(30,30,30,0.98) 0%, rgba(20,20,20,0.95) 100%)'
    : 'linear-gradient(180deg, #fefefe 0%, #f8f6f0 100%)';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerW,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: drawerW,
            boxSizing: 'border-box',
            top: isMobile ? 0 : HEADER_H,
            height: isMobile ? '100vh' : `calc(100vh - ${HEADER_H}px)`,
            zIndex: isMobile ? 1300 : 1200,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
            position: 'fixed',
            ...withLuxuryTexture(
              { background: sidebarBg, borderRight: '2px solid rgba(212,160,23,0.1)', boxShadow: '4px 0 20px rgba(212,160,23,0.08)' },
              luxuryTextures.noise[isDarkMode ? 'dark' : 'light'],
              luxuryTextures.layeredGradient.luxury
            ),
          },
        }}
      >
        {/* Toggle button */}
        <Box sx={{ display: 'flex', justifyContent: drawerOpen ? 'flex-end' : 'center', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)} sx={{ color: '#d4a017', '&:hover': { bgcolor: GOLD_ALPHA } }}>
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        {/* Editing customer banner */}
        {role === 'Sales' && customer?.id && drawerOpen && (
          <Box sx={{
            mx: 1, mb: 1.5, p: 1.5, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(212,160,23,0.1) 0%, rgba(184,134,11,0.05) 100%)',
            border: '1px solid rgba(212,160,23,0.15)',
          }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#8b6914', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
              Editing Customer
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: isDarkMode ? '#fff' : '#2c2c2c', mb: 1 }}>
              {customer.id}{customer.email ? ` – ${customer.email}` : ''}
            </Typography>
            <Button
              size="small" variant="outlined"
              onClick={handleClearCustomer}
              sx={{
                fontSize: '0.72rem', fontWeight: 600, textTransform: 'none',
                borderColor: '#dc143c', color: '#dc143c', borderRadius: 1.5,
                '&:hover': { bgcolor: 'rgba(220,20,60,0.06)', borderColor: '#dc143c' },
              }}
            >
              Exit Customer
            </Button>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ overflowY: 'auto', flex: 1, pb: 3 }}>

          {/* ── Step 1: Select Your Home ── */}
          <NavStep
            drawerOpen={drawerOpen}
            status={step1Status}
            icon={<HomeIcon sx={{ fontSize: 20 }} />}
            label="Select Your Home"
            sublabel={homeComplete ? `${priceData.housePlanName || 'Plan selected'} · Lot ${priceData.lot || priceData.lotnumber}` : 'Choose plan, lot & community'}
            onClick={() => navigate('/customer/selection')}
            isDarkMode={isDarkMode}
          />

          {/* ── Step 2: Build Your Home ── */}
          <Box>
            <NavStep
              drawerOpen={drawerOpen}
              status={step2Status}
              icon={<BuildIcon sx={{ fontSize: 20 }} />}
              label="Build Your Home"
              sublabel={buildHasStarted ? `${completedBuildSections}/${totalBuildSections} sections` : 'Select options in every category'}
              onClick={() => {
                navigate('/customer/selections');
                if (drawerOpen) setBuildExpanded(true);
              }}
              onToggle={drawerOpen ? () => setBuildExpanded((p) => !p) : undefined}
              expanded={buildExpanded}
              isDarkMode={isDarkMode}
            />

            {/* Build progress bar */}
            {drawerOpen && buildHasStarted && (
              <Box sx={{ px: 2, pb: 1 }}>
                <LinearProgress
                  variant="determinate" value={buildPct}
                  sx={{
                    height: 5, borderRadius: 3,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #B8860B 0%, #e6b54a 100%)',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            )}

            {/* Tab sub-items */}
            <Collapse in={drawerOpen && buildExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 2.5, pr: 1, pb: 1 }}>
                {buildTabProgress.map((tab) => {
                  const TabIcon = tab.icon;
                  const allDone = tab.completed === tab.total && tab.total > 0;
                  const hasSome = tab.completed > 0;
                  return (
                    <Box
                      key={tab.id}
                      onClick={() => navigate(`/customer/selections?tab=${tab.id}`)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        py: 0.75, px: 1.5, mb: 0.25, borderRadius: 1.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: GOLD_ALPHA },
                      }}
                    >
                      <TabIcon sx={{ fontSize: 15, color: allDone ? GOLD : hasSome ? GOLD : '#bbb', flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1, fontSize: '0.8rem',
                          color: allDone ? GOLD : isDarkMode ? 'rgba(255,255,255,0.6)' : '#666',
                          fontWeight: allDone ? 600 : 400,
                        }}
                      >
                        {tab.label}
                      </Typography>
                      <Chip
                        label={`${tab.completed}/${tab.total}`}
                        size="small"
                        sx={{
                          height: 16, fontSize: '0.6rem', fontWeight: 700,
                          bgcolor: allDone ? 'rgba(16,185,129,0.15)' : hasSome ? 'rgba(212,160,23,0.15)' : isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                          color: allDone ? '#10B981' : hasSome ? GOLD : 'text.secondary',
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>

          {/* ── Step 3: Review & Finalize ── */}
          <NavStep
            drawerOpen={drawerOpen}
            status={step3Status}
            icon={<SummaryIcon sx={{ fontSize: 20 }} />}
            label="Review & Finalize"
            sublabel={isFinalized ? 'Order submitted' : 'Review and submit your selections'}
            onClick={() => navigate('/customer/selection-summary')}
            isDarkMode={isDarkMode}
          />
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 9, sm: 11 },
          ml: isMobile ? 0 : `${drawerW}px`,
          minHeight: 'calc(100vh - 64px)',
          transition: 'margin-left 0.3s ease',
          bgcolor: isDarkMode ? '#111' : '#fefefe',
        }}
      >
        <PageTransition variant="fadeScale">
          {children}
        </PageTransition>
      </Box>
    </Box>
  );
}

// ── NavStep helper ────────────────────────────────────────────────────────────

function NavStep({ drawerOpen, status, icon, label, sublabel, onClick, onToggle, expanded, isDarkMode }) {
  const textColor = status === 'upcoming'
    ? (isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)')
    : isDarkMode ? '#fff' : '#1a1a1a';

  const handleClick = () => onClick?.();
  const handleToggle = (e) => { e.stopPropagation(); onToggle?.(); };

  const stepIcon = status === 'completed'
    ? <CheckIcon sx={{ fontSize: 20, color: GOLD }} />
    : status === 'current'
    ? <ActiveDotIcon sx={{ fontSize: 20, color: GOLD, animation: 'hubPulse 2s ease-in-out infinite', '@keyframes hubPulse': { '0%,100%': { opacity:1, transform:'scale(1)' }, '50%': { opacity:0.7, transform:'scale(1.15)' } } }} />
    : <EmptyCircleIcon sx={{ fontSize: 20, color: '#bbb' }} />;

  return (
    <Tooltip title={!drawerOpen ? label : ''} placement="right" arrow>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          mx: 1, mb: 0.5, px: 1.5, py: 1.25, borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: status === 'current' ? (isDarkMode ? 'rgba(184,134,11,0.12)' : 'rgba(184,134,11,0.08)') : 'transparent',
          border: status === 'current' ? '1px solid rgba(184,134,11,0.2)' : '1px solid transparent',
          '&:hover': { bgcolor: isDarkMode ? 'rgba(184,134,11,0.15)' : GOLD_ALPHA },
          justifyContent: drawerOpen ? 'flex-start' : 'center',
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{stepIcon}</Box>

        {drawerOpen && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: textColor, lineHeight: 1.2 }}>
                {label}
              </Typography>
              {sublabel && (
                <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', display: 'block', mt: 0.2 }}>
                  {sublabel}
                </Typography>
              )}
            </Box>

            {onToggle && (
              <IconButton size="small" onClick={handleToggle} sx={{ color: GOLD, ml: -0.5 }}>
                {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              </IconButton>
            )}
          </>
        )}
      </Box>
    </Tooltip>
  );
}

export default CustomerLayout;
